import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, CardContent, Divider, Tab, Tabs, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Area, AreaChart } from 'recharts';
import DataTable from '../../components/design/DataTable.jsx';
import useAuth from '../../hooks/useAuth';
import unifiedSocket from '../../services/unifiedSocket';
import { getProductionDashboardSnapshot } from '../../services/contentAiService';

function fmtDate(v) {
  if (!v) return '-';
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
}

function fmtPct(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '-';
  return `${Math.round(n * 100)}%`;
}

function fmtUsd(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '-';
  return `$${n.toFixed(4)}`;
}

function fmtInt(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString('pt-BR');
}

function fmtDuration(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n < 0) return '-';
  const totalSeconds = Math.round(n / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}min`;
  if (minutes > 0) return `${minutes}min ${seconds}s`;
  return `${seconds}s`;
}

function fmtBytes(b) {
  const n = Number(b);
  if (!Number.isFinite(n) || n <= 0) return '-';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function fmtScore(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '-';
  return Math.round(n);
}

const EDITORIAL_SCORE_LABELS = [
  ['scriptScore', 'Roteiro'],
  ['visualScore', 'Visual'],
  ['narrativeScore', 'Narrativa'],
  ['audioScore', 'Audio'],
  ['timelineScore', 'Timeline'],
  ['thumbnailScore', 'Thumb'],
  ['seoScore', 'SEO'],
  ['consistencyScore', 'Consistencia'],
  ['humanExperienceScore', 'Experiencia'],
  ['multimodalScore', 'Multimodal']
];

function formatEditorialScores(scores) {
  const source = scores && typeof scores === 'object' ? scores : {};
  return EDITORIAL_SCORE_LABELS
    .map(([key, label]) => {
      const value = Number(source[key]);
      return Number.isFinite(value) ? `${label}: ${Math.round(value)}` : null;
    })
    .filter(Boolean);
}

function renderTextLines(values, empty = '-') {
  const lines = (Array.isArray(values) ? values : []).filter(Boolean);
  if (!lines.length) return empty;
  return (
    <Box>
      {lines.slice(0, 4).map((line, index) => (
        <Typography key={`${index}-${line}`} variant="caption" display="block">
          {line}
        </Typography>
      ))}
    </Box>
  );
}

const COLORS = ['#4caf50', '#2196f3', '#ff9800', '#9c27b0', '#f44336', '#607d8b', '#795548'];

export default function ProductionDashboardPage() {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const currentUser = authUser?.user || authUser || null;
  const token = authUser?.token || null;
  const socketUserId = currentUser?.id || null;
  const companyId = currentUser?.companyId || 1;

  const [loading, setLoading] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await getProductionDashboardSnapshot();
      setSnapshot(resp?.data || null);
    } catch (e) {
      toast.error(e?.message || 'Falha ao carregar dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!token || !socketUserId) return;
    unifiedSocket.connect({ id: socketUserId, companyId, token });
    const socket = unifiedSocket.getSocket?.();
    if (!socket) return;
    const onMetrics = (payload) => {
      const s = payload?.snapshot || null;
      if (s?.companyId && Number(s.companyId) !== Number(companyId)) return;
      setSnapshot(s);
    };
    socket.on('dashboard.metrics', onMetrics);
    return () => {
      try { socket.off('dashboard.metrics', onMetrics); } catch {}
    };
  }, [token, socketUserId, companyId]);

  const productionCounts = useMemo(() => (snapshot?.metrics?.production?.statusCounts || {}), [snapshot]);
  const pieData = useMemo(() => {
    const keys = Object.keys(productionCounts || {});
    return keys
      .map(k => ({ name: k, value: Number(productionCounts[k] || 0) }))
      .filter(x => x.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [productionCounts]);

  const aiDaily = useMemo(() => {
    const rows = snapshot?.metrics?.ai?.usageDaily || [];
    return (Array.isArray(rows) ? rows : []).map(r => ({
      day: String(r.day || ''),
      requests: Number(r.requests || 0),
      totalTokens: Number(r.totalTokens || 0),
      costUsd: Number(r.costUsd || 0)
    }));
  }, [snapshot]);

  const queueRows = useMemo(() => {
    const rows = snapshot?.queues?.queues || [];
    return Array.isArray(rows) ? rows : [];
  }, [snapshot]);

  const workers = useMemo(() => {
    const rows = snapshot?.workers || [];
    return Array.isArray(rows) ? rows : [];
  }, [snapshot]);

  const recent = useMemo(() => {
    const rows = snapshot?.metrics?.recent || [];
    return Array.isArray(rows) ? rows : [];
  }, [snapshot]);

  const awaitingApproval = useMemo(() => {
    const rows = snapshot?.metrics?.production?.awaitingApproval || [];
    return Array.isArray(rows) ? rows : [];
  }, [snapshot]);

  const rendering = useMemo(() => {
    const rows = snapshot?.metrics?.rendering || [];
    return Array.isArray(rows) ? rows : [];
  }, [snapshot]);

  const recentLogs = useMemo(() => {
    const rows = snapshot?.metrics?.logs?.recent || [];
    return Array.isArray(rows) ? rows : [];
  }, [snapshot]);

  const summary = useMemo(() => {
    const get = (k) => Number(productionCounts?.[k] || 0);
    const failed = snapshot?.metrics?.production?.failed || {};
    const approvalsPending = Number(snapshot?.metrics?.production?.approvalsPending || 0);
    const ytApprovalsPending = Number(snapshot?.metrics?.youtube?.approvalsPending || 0);
    const productionSummaries = snapshot?.metrics?.production?.summaries || {};
    const qTotals = snapshot?.queues?.totals || {};
    const aiToday = snapshot?.metrics?.ai?.today || {};
    return {
      total: Object.values(productionCounts || {}).reduce((a, b) => a + Number(b || 0), 0),
      processing: get('processing'),
      rendering: get('rendering'),
      awaitingApproval: get('awaiting_approval'),
      published: get('published'),
      failedProductions: Number(failed.productions || 0),
      failedStages: Number(failed.stages || 0),
      approvalsPending,
      ytApprovalsPending,
      queueWaiting: Number(qTotals.waiting || 0),
      queueActive: Number(qTotals.active || 0),
      queueFailed: Number(qTotals.failed || 0),
      tokensToday: Number(aiToday.totalTokens || 0),
      requestsToday: Number(aiToday.requests || 0),
      aiCostToday: Number(aiToday.costUsd || 0),
      activeCostUsd: Number(productionSummaries.activeCostUsd || 0),
      activeEtaMs: Number(productionSummaries.activeEtaMs || 0),
      activeElapsedMs: Number(productionSummaries.activeElapsedMs || 0),
      recentScriptScoreAvg: Number(productionSummaries.recentScriptScoreAvg || 0),
      recentThumbnailScoreAvg: Number(productionSummaries.recentThumbnailScoreAvg || 0),
      recentSeoScoreAvg: Number(productionSummaries.recentSeoScoreAvg || 0),
      recentEditorialScoreAvg: Number(productionSummaries.recentEditorialScoreAvg || 0),
      recentCreativeScoreAvg: Number(productionSummaries.recentCreativeScoreAvg || 0),
      editorialPending: Number(snapshot?.metrics?.production?.editorial?.pending || 0),
      editorialReprocessCount: Number(snapshot?.metrics?.production?.editorial?.reprocessCount || 0),
      editorialIssuesCount: Number(snapshot?.metrics?.production?.editorial?.issuesCount || 0),
      editorialTimeSavedMs: Number(snapshot?.metrics?.production?.editorial?.timeSavedMs || 0)
    };
  }, [productionCounts, snapshot]);

  const system = snapshot?.metrics?.system || {};
  const storage = snapshot?.metrics?.storage || {};

  const queueColumns = useMemo(() => ([
    { key: 'name', label: 'Fila', render: (_, r) => r.name },
    { key: 'waiting', label: 'Waiting', render: (_, r) => Number(r.counts?.waiting || 0) },
    { key: 'active', label: 'Active', render: (_, r) => Number(r.counts?.active || 0) },
    { key: 'delayed', label: 'Delayed', render: (_, r) => Number(r.counts?.delayed || 0) },
    { key: 'failed', label: 'Failed', render: (_, r) => Number(r.counts?.failed || 0) },
    { key: 'completed', label: 'Completed', render: (_, r) => Number(r.counts?.completed || 0) }
  ]), []);

  const workerColumns = useMemo(() => ([
    { key: 'type', label: 'Tipo', render: (_, r) => r.type },
    { key: 'queueName', label: 'Fila', render: (_, r) => (r.queueName || '-') },
    { key: 'hostname', label: 'Host', render: (_, r) => r.hostname },
    { key: 'pid', label: 'PID', render: (_, r) => r.pid },
    { key: 'concurrency', label: 'Conc.', render: (_, r) => (r.concurrency != null ? r.concurrency : '-') },
    { key: 'age', label: 'Último', render: (_, r) => (r.ageMs != null ? `${Math.round(Number(r.ageMs) / 1000)}s` : '-') }
  ]), []);

  const prodColumns = useMemo(() => ([
    { key: 'id', label: '#', render: (_, r) => r.id },
    { key: 'title', label: 'Título', render: (_, r) => r.title },
    { key: 'status', label: 'Status', render: (_, r) => r.status },
    { key: 'stage', label: 'Etapa', render: (_, r) => (r.currentStageKey || '-') },
    { key: 'progress', label: 'Progresso', render: (_, r) => fmtPct(r.progressPct) },
    { key: 'queue', label: 'Fila', render: (_, r) => (r.queue || '-') },
    { key: 'eta', label: 'ETA', render: (_, r) => fmtDuration(r.etaMs) },
    { key: 'elapsed', label: 'Tempo', render: (_, r) => fmtDuration(r.elapsedMs || r.totalDurationMs) },
    { key: 'cost', label: 'Custo', render: (_, r) => fmtUsd(r.totalCostUsd) },
    { key: 'tokens', label: 'Tokens', render: () => '-' },
    { key: 'scriptScore', label: 'Score roteiro', render: (_, r) => (r.scriptScore != null ? Math.round(Number(r.scriptScore)) : '-') },
    { key: 'thumbnailScore', label: 'Score thumb', render: (_, r) => (r.thumbnailScore != null ? Math.round(Number(r.thumbnailScore)) : '-') },
    { key: 'seoScore', label: 'Score SEO', render: (_, r) => (r.seoScore != null ? Math.round(Number(r.seoScore)) : '-') },
    { key: 'lastLog', label: 'Último log', render: (_, r) => (r.lastLog?.message || '-') },
    { key: 'updatedAt', label: 'Atualizado', render: (_, r) => fmtDate(r.updatedAt) },
    { key: '_actions', label: 'Ações', render: (_, r) => (
      <Button size="small" variant="outlined" onClick={() => navigate(`/production/${r.id}`)}>Abrir</Button>
    ) }
  ]), [navigate]);

  const renderColumns = useMemo(() => ([
    { key: 'id', label: '#', render: (_, r) => r.id },
    { key: 'title', label: 'Título', render: (_, r) => r.title },
    { key: 'stage', label: 'Etapa', render: (_, r) => (r.currentStageKey || '-') },
    { key: 'renderId', label: 'RenderId', render: (_, r) => (r.render?.renderId || '-') },
    { key: 'renderPct', label: 'Render %', render: (_, r) => (r.render?.progressPct != null ? `${Math.round(Number(r.render.progressPct) * 100)}%` : '-') },
    { key: 'eta', label: 'ETA', render: (_, r) => fmtDuration(r.etaMs) },
    { key: 'elapsed', label: 'Tempo', render: (_, r) => fmtDuration(r.elapsedMs || r.totalDurationMs) },
    { key: 'cost', label: 'Custo', render: (_, r) => fmtUsd(r.totalCostUsd) },
    { key: 'thumbnailScore', label: 'Score thumb', render: (_, r) => (r.thumbnailScore != null ? Math.round(Number(r.thumbnailScore)) : '-') },
    { key: 'seoScore', label: 'Score SEO', render: (_, r) => (r.seoScore != null ? Math.round(Number(r.seoScore)) : '-') },
    { key: 'updatedAt', label: 'Atualizado', render: (_, r) => fmtDate(r.updatedAt) },
    { key: '_actions', label: 'Ações', render: (_, r) => (
      <Button size="small" variant="outlined" onClick={() => navigate(`/production/${r.id}`)}>Abrir</Button>
    ) }
  ]), [navigate]);

  const awaitingColumns = useMemo(() => ([
    { key: 'id', label: '#', render: (_, r) => r.id },
    { key: 'title', label: 'Título', render: (_, r) => r.title },
    { key: 'stage', label: 'Etapa', render: (_, r) => (r.currentStageKey || '-') },
    { key: 'progress', label: 'Progresso', render: (_, r) => fmtPct(r.progressPct) },
    { key: 'eta', label: 'ETA', render: (_, r) => fmtDuration(r.etaMs) },
    { key: 'lastLog', label: 'Último log', render: (_, r) => (r.lastLog?.message || '-') },
    { key: 'updatedAt', label: 'Atualizado', render: (_, r) => fmtDate(r.updatedAt) },
    { key: '_actions', label: 'Ações', render: (_, r) => (
      <Button size="small" variant="outlined" onClick={() => navigate(`/production/${r.id}`)}>Abrir</Button>
    ) }
  ]), [navigate]);

  const logColumns = useMemo(() => ([
    { key: 'createdAt', label: 'Quando', render: (_, r) => fmtDate(r.createdAt) },
    { key: 'productionId', label: 'Projeto', render: (_, r) => (r.productionId != null ? `#${r.productionId}` : '-') },
    { key: 'stageKey', label: 'Etapa', render: (_, r) => (r.stageKey || '-') },
    { key: 'level', label: 'Nível', render: (_, r) => (r.level || 'info') },
    { key: 'message', label: 'Mensagem', render: (_, r) => (r.message || '-') }
  ]), []);

  const queueChartData = useMemo(() => queueRows.map(q => ({
    name: q.name,
    waiting: Number(q.counts?.waiting || 0),
    active: Number(q.counts?.active || 0),
    failed: Number(q.counts?.failed || 0)
  })), [queueRows]);

  const editorialRows = useMemo(() => {
    const map = new Map();
    [...awaitingApproval, ...recent].forEach((row) => {
      const id = Number(row?.id || 0);
      if (!id) return;
      const hasEditorialData = (
        row?.editorialScore != null ||
        row?.approvalStatus ||
        row?.currentStageKey === 'editorial_review' ||
        (Array.isArray(row?.detectedIssuesPreview) && row.detectedIssuesPreview.length > 0) ||
        (Array.isArray(row?.resolvedIssuesPreview) && row.resolvedIssuesPreview.length > 0)
      );
      if (!hasEditorialData) return;
      if (!map.has(id)) map.set(id, row);
    });
    return [...map.values()].sort((a, b) => new Date(b?.updatedAt || 0).getTime() - new Date(a?.updatedAt || 0).getTime());
  }, [awaitingApproval, recent]);

  const editorialColumns = useMemo(() => ([
    { key: 'id', label: '#', render: (_, r) => r.id },
    { key: 'title', label: 'Titulo', render: (_, r) => r.title },
    { key: 'approvalStatus', label: 'Decisao', render: (_, r) => (r.approvalStatus || '-') },
    { key: 'editorialScore', label: 'Qualidade Geral', render: (_, r) => fmtScore(r.editorialScore) },
    { key: 'editorialModules', label: 'Notas Individuais', render: (_, r) => renderTextLines(formatEditorialScores(r.editorialModules)) },
    { key: 'detectedIssuesPreview', label: 'Problemas', render: (_, r) => renderTextLines(r.detectedIssuesPreview) },
    { key: 'resolvedIssuesPreview', label: 'Correcoes', render: (_, r) => renderTextLines(r.resolvedIssuesPreview) },
    { key: 'reprocessCount', label: 'Reprocess.', render: (_, r) => fmtInt(r.reprocessCount) },
    { key: 'timeSavedMs', label: 'Tempo economizado', render: (_, r) => fmtDuration(r.timeSavedMs) },
    { key: 'approvedVersion', label: 'Versao aprovada', render: (_, r) => (r.approvedVersion || '-') },
    { key: 'lastReviewedAt', label: 'Revisado', render: (_, r) => fmtDate(r.lastReviewedAt) },
    { key: '_actions', label: 'Acoes', render: (_, r) => (
      <Button size="small" variant="outlined" onClick={() => navigate(`/production/${r.id}`)}>Abrir</Button>
    ) }
  ]), [navigate]);

  const dashboardTitle = `Dashboard Produção IA${snapshot?.generatedAt ? ` — ${fmtDate(snapshot.generatedAt)}` : ''}`;

  return (
    <Box p={2} display="grid" gap={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h5">{dashboardTitle}</Typography>
          <Typography variant="body2">Realtime: {snapshot ? 'conectado' : 'carregando'} • Host: {system.hostname || '-'}</Typography>
        </Box>
        <Button variant="outlined" onClick={load} disabled={loading}>Atualizar</Button>
      </Box>

      <Card>
        <CardContent sx={{ pb: 1 }}>
          <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)}>
            <Tab value="overview" label="Visao Geral" />
            <Tab value="editorial" label="Editorial Review" />
          </Tabs>
        </CardContent>
      </Card>

      {activeTab === 'overview' ? (
        <>
          <Box display="grid" gap={2} gridTemplateColumns="repeat(12, 1fr)">
            {[
              { label: 'Produções', value: summary.total },
              { label: 'Processando', value: summary.processing },
              { label: 'Renderizando', value: summary.rendering },
              { label: 'Aguardando aprovação', value: summary.awaitingApproval },
              { label: 'Publicados', value: summary.published },
              { label: 'Falhas (prod)', value: summary.failedProductions },
              { label: 'Falhas (etapas)', value: summary.failedStages },
              { label: 'Approvals pendentes', value: summary.approvalsPending },
              { label: 'Publishing pendente', value: summary.ytApprovalsPending },
              { label: 'Fila waiting', value: summary.queueWaiting },
              { label: 'Fila active', value: summary.queueActive },
              { label: 'Fila failed', value: summary.queueFailed }
            ].map((c, idx) => (
              <Card key={idx} sx={{ gridColumn: 'span 3' }}>
                <CardContent>
                  <Typography variant="body2" color="textSecondary">{c.label}</Typography>
                  <Typography variant="h6">{c.value}</Typography>
                </CardContent>
              </Card>
            ))}
          </Box>

          <Box display="grid" gap={2} gridTemplateColumns="repeat(12, 1fr)">
            {[
              { label: 'Custo IA hoje', value: fmtUsd(summary.aiCostToday) },
              { label: 'Custo produções visíveis', value: fmtUsd(summary.activeCostUsd) },
              { label: 'Tokens hoje', value: fmtInt(summary.tokensToday) },
              { label: 'Requests hoje', value: fmtInt(summary.requestsToday) },
              { label: 'Tempo ativo', value: fmtDuration(summary.activeElapsedMs) },
              { label: 'ETA ativo', value: fmtDuration(summary.activeEtaMs) },
              { label: 'Score roteiro médio', value: summary.recentScriptScoreAvg ? Math.round(summary.recentScriptScoreAvg) : '-' },
              { label: 'Score thumbnail médio', value: summary.recentThumbnailScoreAvg ? Math.round(summary.recentThumbnailScoreAvg) : '-' },
              { label: 'Score SEO médio', value: summary.recentSeoScoreAvg ? Math.round(summary.recentSeoScoreAvg) : '-' },
              { label: 'Score criativo médio', value: summary.recentCreativeScoreAvg ? Math.round(summary.recentCreativeScoreAvg) : '-' }
            ].map((c, idx) => (
              <Card key={`extra-${idx}`} sx={{ gridColumn: 'span 4' }}>
                <CardContent>
                  <Typography variant="body2" color="textSecondary">{c.label}</Typography>
                  <Typography variant="h6">{c.value}</Typography>
                </CardContent>
              </Card>
            ))}
          </Box>

          <Box display="grid" gap={2} gridTemplateColumns="repeat(12, 1fr)">
            <Card sx={{ gridColumn: 'span 4' }}>
              <CardContent>
                <Typography variant="h6">Produção (status)</Typography>
                <Divider sx={{ my: 1 }} />
                <Box height={260}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={90} label>
                        {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ gridColumn: 'span 4' }}>
              <CardContent>
                <Typography variant="h6">Consumo IA (14 dias)</Typography>
                <Divider sx={{ my: 1 }} />
                <Box height={260}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={aiDaily}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Area type="monotone" dataKey="totalTokens" stroke="#2196f3" fill="#2196f3" fillOpacity={0.2} name="Tokens" />
                      <Area type="monotone" dataKey="requests" stroke="#4caf50" fill="#4caf50" fillOpacity={0.15} name="Requests" />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
                <Typography variant="body2">
                  Hoje: {snapshot?.metrics?.ai?.today ? `${snapshot.metrics.ai.today.requests} req • ${snapshot.metrics.ai.today.totalTokens} tokens • $${Number(snapshot.metrics.ai.today.costUsd || 0).toFixed(4)}` : '-'}
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ gridColumn: 'span 4' }}>
              <CardContent>
                <Typography variant="h6">Recursos</Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2">CPU: {system.cpuUsagePct != null ? fmtPct(system.cpuUsagePct) : '-'}</Typography>
                <Typography variant="body2">RAM: {system.memory?.usedPct != null ? fmtPct(system.memory.usedPct) : '-'} ({fmtBytes(system.memory?.usedBytes)} / {fmtBytes(system.memory?.totalBytes)})</Typography>
                <Typography variant="body2">Uploads: {storage.uploads?.usedPct != null ? fmtPct(storage.uploads.usedPct) : '-'} ({fmtBytes(storage.uploads?.usedBytes)} / {fmtBytes(storage.uploads?.totalBytes)})</Typography>
                <Typography variant="body2">Previews: {storage.previews?.usedPct != null ? fmtPct(storage.previews.usedPct) : '-'} ({fmtBytes(storage.previews?.usedBytes)} / {fmtBytes(storage.previews?.totalBytes)})</Typography>
                <Typography variant="body2">GPU: {snapshot?.metrics?.system?.gpu?.gpus?.length ? `${snapshot.metrics.system.gpu.gpus.length} GPU(s)` : '-'}</Typography>
              </CardContent>
            </Card>
          </Box>

          <Card>
            <CardContent>
              <Typography variant="h6">Monitor de filas</Typography>
              <Divider sx={{ my: 1 }} />
              <Box height={220}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={queueChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="waiting" fill="#ff9800" name="Waiting" />
                    <Bar dataKey="active" fill="#2196f3" name="Active" />
                    <Bar dataKey="failed" fill="#f44336" name="Failed" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
              <DataTable rows={queueRows} columns={queueColumns} getKey={(r) => r.name} />
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6">Workers ativos</Typography>
              <Divider sx={{ my: 1 }} />
              <DataTable rows={workers} columns={workerColumns} getKey={(r) => `${r.type}:${r.queueName || ''}:${r.hostname}:${r.pid}`} />
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6">Renderizando</Typography>
              <Divider sx={{ my: 1 }} />
              <DataTable rows={rendering} columns={renderColumns} getKey={(r) => r.id} />
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6">Aguardando aprovação</Typography>
              <Divider sx={{ my: 1 }} />
              <DataTable rows={awaitingApproval} columns={awaitingColumns} getKey={(r) => r.id} />
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6">Últimas produções</Typography>
              <Divider sx={{ my: 1 }} />
              <DataTable rows={recent} columns={prodColumns} getKey={(r) => r.id} />
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6">Logs recentes</Typography>
              <Divider sx={{ my: 1 }} />
              <DataTable rows={recentLogs} columns={logColumns} getKey={(r) => r.id} />
            </CardContent>
          </Card>
        </>
      ) : null}

      {activeTab === 'editorial' ? (
        <>
          <Box display="grid" gap={2} gridTemplateColumns="repeat(12, 1fr)">
            {[
              { label: 'Qualidade geral media', value: summary.recentEditorialScoreAvg ? Math.round(summary.recentEditorialScoreAvg) : '-' },
              { label: 'Producoes em editorial', value: summary.editorialPending },
              { label: 'Problemas encontrados', value: summary.editorialIssuesCount },
              { label: 'Correcoes via retry', value: summary.editorialReprocessCount },
              { label: 'Tempo economizado', value: fmtDuration(summary.editorialTimeSavedMs) },
              { label: 'Producoes auditadas', value: editorialRows.length }
            ].map((c, idx) => (
              <Card key={`editorial-${idx}`} sx={{ gridColumn: 'span 4' }}>
                <CardContent>
                  <Typography variant="body2" color="textSecondary">{c.label}</Typography>
                  <Typography variant="h6">{c.value}</Typography>
                </CardContent>
              </Card>
            ))}
          </Box>

          <Card>
            <CardContent>
              <Typography variant="h6">Editorial Review</Typography>
              <Divider sx={{ my: 1 }} />
              <DataTable rows={editorialRows} columns={editorialColumns} getKey={(r) => r.id} />
            </CardContent>
          </Card>
        </>
      ) : null}
    </Box>
  );
}
